/** Board geometry shared by the web and iPhone renderers (pure functions, no UI). */
import { cardRank, cardSuit } from "./games/jackaroo.js";

export type Pt = [number, number];

/* ── Ludo: 15×15 grid ── */
export const LUDO_PATH: Pt[] = [
  [1, 6], [2, 6], [3, 6], [4, 6], [5, 6], [6, 5], [6, 4], [6, 3], [6, 2], [6, 1], [6, 0], [7, 0],
  [8, 0], [8, 1], [8, 2], [8, 3], [8, 4], [8, 5], [9, 6], [10, 6], [11, 6], [12, 6], [13, 6], [14, 6], [14, 7],
  [14, 8], [13, 8], [12, 8], [11, 8], [10, 8], [9, 8], [8, 9], [8, 10], [8, 11], [8, 12], [8, 13], [8, 14], [7, 14],
  [6, 14], [6, 13], [6, 12], [6, 11], [6, 10], [6, 9], [5, 8], [4, 8], [3, 8], [2, 8], [1, 8], [0, 8], [0, 7], [0, 6],
];
export const LUDO_HOME: Record<number, Pt[]> = {
  0: [[1, 7], [2, 7], [3, 7], [4, 7], [5, 7]],
  1: [[7, 1], [7, 2], [7, 3], [7, 4], [7, 5]],
  2: [[13, 7], [12, 7], [11, 7], [10, 7], [9, 7]],
  3: [[7, 13], [7, 12], [7, 11], [7, 10], [7, 9]],
};
export const LUDO_YARD: Record<number, Pt> = { 0: [0, 0], 1: [9, 0], 2: [9, 9], 3: [0, 9] };
export const LUDO_SAFE = new Set([0, 8, 13, 21, 26, 34, 39, 47]);
const LUDO_FINISH: Record<number, Pt> = { 0: [6.6, 7.5], 1: [7.5, 6.6], 2: [8.4, 7.5], 3: [7.5, 8.4] };

/** Centre of a token in grid units. */
export function ludoCell(quadrant: number, progress: number, token: number): Pt {
  if (progress < 0) {
    const [x, y] = LUDO_YARD[quadrant];
    return [x + 1.75 + (token % 2) * 2.5, y + 1.75 + Math.floor(token / 2) * 2.5];
  }
  if (progress <= 50) {
    const [x, y] = LUDO_PATH[(quadrant * 13 + progress) % 52];
    return [x + 0.5, y + 0.5];
  }
  if (progress <= 55) {
    const [x, y] = LUDO_HOME[quadrant][progress - 51];
    return [x + 0.5, y + 0.5];
  }
  return LUDO_FINISH[quadrant];
}

/* ── Ludo: what each player sees ──
 * Movement is CLOCKWISE on screen (the worldwide Ludo convention, as in Ludo King and Yalla Ludo).
 * Quadrants run clockwise: 0 top-left, 1 top-right, 2 bottom-right, 3 bottom-left, and seats take
 * them in turn order, so the next player is always the next yard along the direction of travel.
 * Every player sees their OWN yard at the bottom-left: the board is rotated for them (never mirrored),
 * exactly like a real board turned towards you. The engine never sees this rotation.
 */
export const LUDO_N = 15;
/** Quarter-turns clockwise on screen around the board centre. */
export const ludoRot = ([x, y]: Pt, q: number): Pt => {
  let a = x - LUDO_N / 2;
  let b = y - LUDO_N / 2;
  for (let i = 0; i < ((q % 4) + 4) % 4; i++) [a, b] = [-b, a];
  return [a + LUDO_N / 2, b + LUDO_N / 2];
};
/** Quarter-turns that bring `myQuadrant` to the bottom-left. */
export const ludoViewTurns = (myQuadrant: number) => (((3 - myQuadrant) % 4) + 4) % 4;
/** The cells a token passes through for one move (for the hop-by-hop animation), canonical frame. */
export function ludoTrail(quadrant: number, from: number, to: number, token: number): Pt[] {
  if (from < 0) return [ludoCell(quadrant, -1, token), ludoCell(quadrant, 0, token)];
  const out: Pt[] = [];
  for (let p = from; p <= to; p++) out.push(ludoCell(quadrant, p, token));
  return out;
}

/* ── Jackaroo: 20×20 square loop, 19 squares a side ──
 * ONE geometry for every renderer (web and iPhone). Never re-derive it in a UI file:
 * the web board used its own mirrored copy, so the same server state was drawn
 * clockwise on the web and counter-clockwise on the phone.
 *
 * Direction: marbles travel, and turns pass, COUNTER-CLOCKWISE on screen — play
 * passes to the player on your right, the Gulf convention for Jackaroo. The engine
 * only knows "forward" (+1) and "the next seat" (+1); this file is where those two
 * become a direction on screen, and both map to the same one.
 *
 * Canonical frame (board side 0 at the bottom, y grows downward):
 *  - side k is side 0 turned k quarter-turns counter-clockwise, so seat order
 *    0 → 1 → 2 → 3 is bottom → right → top → left;
 *  - a side's 19 squares run left → right along its bottom edge; square 0 (the
 *    bottom-left corner) is that side's START square, where marbles come out;
 *  - progress 75 is the square just before the start (end of the left edge, coming
 *    down), and the home lane leaves the track at that corner, running diagonally
 *    inward: a marble enters home after one full lap;
 *  - the base (4 marbles not yet out) sits in the middle of the side, inside the track.
 */
export const JK_N = 20;
/** Quarter-turns counter-clockwise on screen, around the board centre. */
export const jkRot = ([x, y]: Pt, q: number): Pt => {
  let a = x - JK_N / 2;
  let b = y - JK_N / 2;
  for (let i = 0; i < ((q % 4) + 4) % 4; i++) [a, b] = [b, -a];
  return [a + JK_N / 2, b + JK_N / 2];
};
export const jkTrackPt = (abs: number): Pt => jkRot([(abs % 19) + 0.5, 19.5], Math.floor(abs / 19));
export const jkHomePt = (side: number, h: number): Pt => jkRot([1.75 + h * 1.08, 18.25 - h * 1.08], side);
export const jkBasePt = (side: number, i: number): Pt => jkRot([9.35 + (i % 2) * 1.3, 15.9 + Math.floor(i / 2) * 1.3], side);
/** Where a seat's name plate sits (inside the track, just above its base). */
export const jkPlatePt = (side: number): Pt => jkRot([10, 13.9], side);
export function jkMarblePt(boardPos: number, p: number, i: number): Pt {
  if (p < 0) return jkBasePt(boardPos, i);
  if (p >= 76) return jkHomePt(boardPos, p - 76);
  return jkTrackPt((boardPos * 19 + p) % 76);
}
/** Quarter-turns that bring `myBoardPos` to the bottom of the screen. Rotation only: never a mirror. */
export const jkViewTurns = (myBoardPos: number) => (4 - (myBoardPos % 4)) % 4;
/** Unit vector of travel at a track square (towards the next square), in canonical coordinates. */
export function jkTravelDir(abs: number): Pt {
  const a = jkTrackPt(abs);
  const b = jkTrackPt((abs + 1) % 76);
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy) || 1;
  return [dx / len, dy / len];
}

export interface JkRef { s: number; i: number }
interface JkViewLike { n: number; boardPos: number[]; marbles: number[][]; teams?: boolean }
/**
 * Where each marble a legal move touches ends up (captures not included). Pure arithmetic on
 * the same rules as the engine; only used to preview a move the server already listed as legal.
 */
export function jkPreview(view: JkViewLike, seat: number, move: any): { ref: JkRef; from: number; to: number }[] {
  const teams = view.teams ?? view.n === 4;
  const home = (s: number) => view.marbles[s].every((p) => p >= 76);
  const cs = teams && home(seat) ? (seat + 2) % 4 : seat;
  const at = (r: JkRef) => view.marbles[r.s]?.[r.i];
  const fwd = (r: JkRef, k: number) => ({ ref: r, from: at(r), to: at(r) + k });
  if (!move || move.type !== "play") return [];
  switch (move.action) {
    case "exit": {
      const i = view.marbles[cs].indexOf(-1);
      return i < 0 ? [] : [{ ref: { s: cs, i }, from: -1, to: 0 }];
    }
    case "move":
      return [fwd(move.marble, move.steps)];
    case "back":
      return [{ ref: move.marble, from: at(move.marble), to: (at(move.marble) - 4 + 76) % 76 }];
    case "swap": {
      const a: JkRef = move.marble;
      const b: JkRef = move.target;
      const absA = (view.boardPos[a.s] * 19 + at(a)) % 76;
      const absB = (view.boardPos[b.s] * 19 + at(b)) % 76;
      return [
        { ref: a, from: at(a), to: (absB - view.boardPos[a.s] * 19 + 76) % 76 },
        { ref: b, from: at(b), to: (absA - view.boardPos[b.s] * 19 + 76) % 76 },
      ];
    }
    case "split":
      return (move.parts ?? []).map((p: any) => fwd(p.marble, p.steps));
  }
  return [];
}

export const CARD_RANK = ["", "A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];
export const CARD_SUIT = ["♠", "♥", "♦", "♣"];
export const cardLabel = (c: number) => ({ rank: CARD_RANK[cardRank(c)], suit: CARD_SUIT[cardSuit(c)], red: cardSuit(c) === 1 || cardSuit(c) === 2 });
